/**
 * Export the asset as a PDF: the client version (shareable content and anonymized, confirmed-shareable cases) for
 * anyone with `asset.export`, and the internal one with `asset.view.internal` as well. The PDF opens in a new tab;
 * a refusal (ten exports a minute) shows as a toast through the mutation's failure message.
 */

import { FileDown, Globe, Lock } from 'lucide-react';

import { useExportPdf } from '@/api/queries/exports';
import type { AssetOut } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { MenuButton, type RowMenuItem } from '@/ui/RowMenu';

export function ExportMenu({ asset }: { asset: AssetOut }) {
  const { can } = useSession();
  const exportPdf = useExportPdf(asset.id);
  if (!can('asset.export')) return null;

  const items: RowMenuItem[] = [
    {
      label: 'Export PDF for clients',
      icon: <Globe aria-hidden="true" />,
      onSelect: () => exportPdf.mutate('external'),
      disabled: exportPdf.isPending,
    },
  ];
  if (can('asset.view.internal')) {
    items.push({
      label: 'Export internal PDF',
      icon: <Lock aria-hidden="true" />,
      onSelect: () => exportPdf.mutate('internal'),
      disabled: exportPdf.isPending,
    });
  }
  return (
    <MenuButton
      label="Export"
      items={items}
      trigger={{ text: exportPdf.isPending ? 'Exporting…' : 'Export PDF', icon: <FileDown aria-hidden="true" />, className: 'btn-secondary btn-sm' }}
    />
  );
}
